import { useState, useMemo, useRef, useEffect, useCallback } from "react";

// ─── API CONFIG ────────────────────────────────────────────────────────────────
const API_BASE = (() => {
    if (typeof window !== "undefined" && window.config?.base_api_url) {
        return window.config.base_api_url;
    }
    const env = process.env;
    if (env.NODE_ENV === "production") {
        return window.location.protocol === "https:"
            ? env.REACT_APP_API_HTTPS_URL
            : env.REACT_APP_API_HTTP_URL;
    }
    return "/application";
})();

async function apiFetch(path, params = {}) {
    const url = new URL(API_BASE + path, window.location.origin);
    Object.entries(params).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) url.searchParams.set(k, v);
    });
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
    return res.json();
}

// ─── HELPERS ───────────────────────────────────────────────────────────────────
const MONTHS_TH = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseDate(str) {
    if (!str) return null;
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
}
function formatDisplay(str) {
    if (!str) return "";
    const d = parseDate(str);
    if (!d) return str;
    return `${String(d.getDate()).padStart(2, "0")} ${MONTHS_TH[d.getMonth()]} ${d.getFullYear()}`;
}
function toYMD(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function fmtVal(n) {
    if (!n || n === 0) return "–";
    if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
    if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(0) + "K";
    return n.toLocaleString();
}

// ─── PROCESS DATA ──────────────────────────────────────────────────────────────
// Raw: แสดงทุก row ตามจริง ไม่รวม — ใช้ก่อนกด Check
function processDataRaw(records) {
    return records.map(r => ({
        date: r.transaction_date,
        symbol: r.symbol,
        name: r.name,
        buy: (r["transaction_method"] == "ซื้อ" || r["transaction_method"] == "รับโอน") ? r["sum(amount)"] * r.avg_price : 0,
        sell: (r["transaction_method"] == "ขาย" || r["transaction_method"] == "โอนออก") ? r["sum(amount)"] * r.avg_price : 0,
        closePrice: r.Close,
        price: r.Close,
        avg_price: r.avg_price
    }));
}

// Agg: รวม value ของ date|symbol เดียวกัน — ใช้หลังกด Check
function processDataAgg(records) {
    const groups = {};
    records.forEach(r => {
        const key = r.transaction_date + "|" + r.symbol;
        if (!groups[key]) {
            groups[key] = {
                date: r.transaction_date,
                symbol: r.symbol,
                names: new Set(),
                buy: 0,
                sell: 0,
                closePrice: r.Close,
                price: r.Close,
                avg_price: r.avg_price
            };
        }
        groups[key].names.add(r.name);
        // groups[key].buy += r["sum(amount)"] * r.Close;
        // ใน processDataAgg
        const method = r["transaction_method"];
        const value = r["sum(amount)"] * r.avg_price;
        if (method === "ซื้อ" || method === "รับโอน") {
            groups[key].buy += value;
        } else if (method === "ขาย" || method === "โอนออก") {
            groups[key].sell += value;
        }
        if (r.Close > 0) {
            groups[key].closePrice = r.Close;
            groups[key].price = r.Close;
        }
    });
    return Object.values(groups).map(g => ({
        ...g,
        name: [...g.names].join(" / "),
    }));
}

// ─── DATE PICKER ───────────────────────────────────────────────────────────────
function DatePicker({ value, onChange, minDate, maxDate }) {
    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() => { const d = parseDate(value); return d ? d.getFullYear() : 2026; });
    const [viewMonth, setViewMonth] = useState(() => { const d = parseDate(value); return d ? d.getMonth() : 3; });
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowMonthPicker(false); } };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    useEffect(() => {
        if (value) { const d = parseDate(value); if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); } }
    }, [value]);

    const minD = parseDate(minDate);
    const maxD = parseDate(maxDate);
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = Array(firstDay).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
    const todayStr = toYMD(new Date());

    return (
        <div style={{ position: "relative", display: "inline-block" }} ref={ref}>
            <style>{`.dp-cell:hover{background:#1e3a5f!important}.dp-mth:hover{background:#1e2d45!important}@keyframes dpFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
            <div onClick={() => { setOpen(o => !o); setShowMonthPicker(false); }}
                style={{ display: "flex", alignItems: "center", gap: 8, background: "#111d30", border: "1px solid #1e2d45", borderRadius: 6, padding: "0 10px", height: 32, minWidth: 160, cursor: "pointer", color: value ? "#c9d4e8" : "#3a506a", fontSize: 12, userSelect: "none" }}>
                <span style={{ color: "#2563eb", fontSize: 14 }}>◈</span>
                <span style={{ flex: 1 }}>{value ? formatDisplay(value) : "เลือกวันที่"}</span>
                <span style={{ color: "#3a506a", fontSize: 10 }}>{open ? "▲" : "▼"}</span>
            </div>
            {open && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 999, background: "#0f1c2e", border: "1px solid #1e2d45", borderRadius: 12, padding: "16px 14px 12px", boxShadow: "0 12px 40px rgba(0,0,0,.7)", width: 290, animation: "dpFade .15s ease" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                        <button onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); }} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 6px" }}>‹</button>
                        <div onClick={() => setShowMonthPicker(p => !p)} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                            <span style={{ color: "#c9d4e8", fontWeight: 700, fontSize: 15 }}>{MONTHS_FULL[viewMonth]} {viewYear}</span>
                            <span style={{ color: "#5a7090", fontSize: 11 }}>▾</span>
                        </div>
                        <button onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); }} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 6px" }}>›</button>
                    </div>
                    {showMonthPicker && (
                        <div style={{ marginBottom: 14, background: "#0b1422", borderRadius: 8, padding: "10px 8px" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 10 }}>
                                <button onClick={() => setViewYear(y => y - 1)} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>‹</button>
                                <span style={{ color: "#c9d4e8", fontWeight: 700, fontSize: 14, minWidth: 48, textAlign: "center" }}>{viewYear}</span>
                                <button onClick={() => setViewYear(y => y + 1)} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>›</button>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4 }}>
                                {MONTHS_TH.map((m, i) => (
                                    <div key={i} className="dp-mth" onClick={() => { setViewMonth(i); setShowMonthPicker(false); }}
                                        style={{ textAlign: "center", padding: "7px 0", borderRadius: 6, fontSize: 12, cursor: "pointer", background: i === viewMonth ? "#2563eb" : "transparent", color: i === viewMonth ? "#fff" : "#a0b4cc", fontWeight: i === viewMonth ? 700 : 400, transition: "background .1s" }}>
                                        {m}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 6 }}>
                        {DAYS_LABEL.map(d => <div key={d} style={{ textAlign: "center", fontSize: 11, color: "#4a6080", fontWeight: 600, padding: "2px 0" }}>{d}</div>)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)" }}>
                        {cells.map((day, i) => {
                            if (!day) return <div key={i} />;
                            const d = new Date(viewYear, viewMonth, day);
                            const dStr = toYMD(d);
                            const isSelected = dStr === value;
                            const isDisabled = (minDate && dStr < minDate) || (maxDate && dStr > maxDate);
                            const isToday = dStr === todayStr;
                            return (
                                <div key={i} className={isDisabled ? "" : "dp-cell"}
                                    onClick={() => { if (!isDisabled) { onChange(toYMD(new Date(viewYear, viewMonth, day))); setOpen(false); setShowMonthPicker(false); } }}
                                    style={{ textAlign: "center", padding: "5px 0 4px", borderRadius: 8, cursor: isDisabled ? "not-allowed" : "pointer", background: isSelected ? "#2563eb" : "transparent", color: isDisabled ? "#253040" : isSelected ? "#fff" : isToday ? "#60a5fa" : "#c9d4e8", fontWeight: (isSelected || isToday) ? 700 : 400, fontSize: 13, transition: "background .1s" }}>
                                    <div>{day}</div>
                                    <div style={{ width: 4, height: 4, borderRadius: "50%", margin: "2px auto 0", background: isDisabled ? "transparent" : isSelected ? "rgba(255,255,255,0.6)" : "#2563eb" }} />
                                </div>
                            );
                        })}
                    </div>
                    {value && (
                        <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #1e2d45", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 11, color: "#5a7090" }}>{formatDisplay(value)}</span>
                            <button onClick={() => { onChange(""); setOpen(false); }} style={{ background: "transparent", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 11, padding: "2px 6px" }}>ล้าง</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── SYMBOL SELECT ─────────────────────────────────────────────────────────────
function SymbolSelect({ symbols, value, onChange, onCheckboxClick, isAllChecked, hasData }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const checked = isAllChecked || !!value;
    const label = isAllChecked ? "All" : (value || "Type a Symbol");
    const labelColor = isAllChecked ? "#c9d4e8" : (value ? "#60a5fa" : "#3a506a");

    return (
        <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
            <div
                onMouseDown={e => {
                    e.preventDefault();
                    if (e.target.closest("[data-cb]")) { onCheckboxClick && onCheckboxClick(); return; }
                    if (hasData) setOpen(o => !o);
                }}
                style={{ display: "flex", alignItems: "center", gap: 9, background: "#111d30", border: "1px solid #1e2d45", borderRadius: 8, padding: "0 32px 0 10px", height: 32, minWidth: 160, cursor: hasData ? "pointer" : "default", fontSize: 12, userSelect: "none", position: "relative" }}>
                <div data-cb="1" style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, border: "1px solid #3a506a", background: checked ? "#2563eb" : "#0b1422", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    {checked && <svg width="9" height="6" viewBox="0 0 9 6" fill="none"><path d="M1 3L3.5 5.5L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" /></svg>}
                </div>
                <span style={{ flex: 1, color: labelColor, fontWeight: value && !isAllChecked ? 700 : 400 }}>{label}</span>
                {hasData && <span style={{ position: "absolute", right: 10, top: "50%", transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, color: "#3a506a", fontSize: 10, pointerEvents: "none", transition: "transform .15s" }}>▾</span>}
            </div>
            {open && hasData && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#0f1c2e", border: "1px solid #1e2d45", borderRadius: 10, zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,.5)", maxHeight: 220, overflowY: "auto" }}>
                    {symbols.map(s => (
                        <div key={s} onMouseDown={e => { e.preventDefault(); onChange(s); setOpen(false); }}
                            style={{ padding: "8px 14px", fontSize: 12, cursor: "pointer", color: value === s ? "#60a5fa" : "#c9d4e8", fontWeight: value === s ? 700 : 400 }}
                            onMouseEnter={e => e.currentTarget.style.background = "#1e3a5f"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {s}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── NAME CELL ─────────────────────────────────────────────────────────────────
function NameCell({ name }) {
    const [show, setShow] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    if (!name) return <span style={{ color: "#3a506a" }}>–</span>;
    const parts = name.split(" / ");
    const isTruncated = parts.length > 1 || name.length > 22;

    function handleMouseMove(e) { setPos({ x: e.clientX, y: e.clientY }); }

    const inner = parts.length === 1 ? (
        <span style={{ display: "block", maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
    ) : (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{parts[0]}</span>
            <span style={{ flexShrink: 0, background: "#1e3a5f", color: "#60a5fa", fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 5px", lineHeight: "16px" }}>+{parts.length - 1}</span>
        </span>
    );

    if (!isTruncated) return inner;

    return (
        <span style={{ cursor: "default", display: "block" }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
            onMouseMove={handleMouseMove}>
            {inner}
            {show && (
                <div style={{ position: "fixed", left: pos.x + 12, top: pos.y + 12, zIndex: 9999, background: "#0f1c2e", border: "1px solid #2563eb", borderRadius: 8, padding: "8px 12px", boxShadow: "0 8px 32px rgba(0,0,0,.7)", pointerEvents: "none", maxWidth: 280 }}>
                    {parts.map((p, i) => (
                        <div key={i} style={{ color: "#c9d4e8", fontSize: 12, padding: "2px 0", borderBottom: i < parts.length - 1 ? "1px solid #1e2d45" : "none", paddingBottom: i < parts.length - 1 ? 4 : 0, marginBottom: i < parts.length - 1 ? 4 : 0, whiteSpace: "nowrap" }}>{p}</div>
                    ))}
                </div>
            )}
        </span>
    );
}

// ─── SORT ARROW ────────────────────────────────────────────────────────────────
function SortArrow({ col, sortCol, sortDir }) {
    const active = sortCol === col;
    return <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3, fontSize: 10 }}>{active ? (sortDir === 1 ? "↑" : "↓") : "⇅"}</span>;
}

// ─── SPINNER ───────────────────────────────────────────────────────────────────
function Spinner() {
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60, gap: 14 }}>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <div style={{ width: 32, height: 32, border: "3px solid #1e2d45", borderTop: "3px solid #2563eb", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
            <span style={{ color: "#3a506a", fontSize: 12 }}>กำลังโหลดข้อมูล…</span>
        </div>
    );
}

// ─── ERROR BANNER ──────────────────────────────────────────────────────────────
function ErrorBanner({ message, onRetry }) {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: 60, flexDirection: "column" }}>
            <span style={{ fontSize: 28 }}>⚠️</span>
            <span style={{ color: "#ef4444", fontSize: 13 }}>{message}</span>
            {onRetry && <button onClick={onRetry} style={{ marginTop: 4, background: "#2563eb", border: "none", borderRadius: 6, color: "#fff", padding: "6px 18px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>ลองใหม่</button>}
        </div>
    );
}

// ─── MAIN ──────────────────────────────────────────────────────────────────────
export default function Form59Dashboard() {
    const [startDate, setStartDate] = useState("");
    // const [startDate, setStartDate] = useState(() => {
    //     const now = new Date();
    //     return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    // });
    const [endDate, setEndDate] = useState(() => toYMD(new Date()));
    // const [endDate, setEndDate] = useState("");
    const [symbolFilter, setSymbolFilter] = useState("");
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [minValue, setMinValue] = useState("");
    const [sortCol, setSortCol] = useState("date");
    const [sortDir, setSortDir] = useState(-1);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const [rawRecords, setRawRecords] = useState([]);
    const [stats] = useState({ date_min: "2011-01-28", date_max: toYMD(new Date()) });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sumValueSymbols, setSumValueSymbols] = useState([]);

    const tableWrapRef = useRef(null);

    // ── Auto page-size ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!tableWrapRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const h = entry.contentRect.height;
                setPageSize(Math.max(5, Math.floor((h - 76) / 36.5)));
            }
        });
        observer.observe(tableWrapRef.current);
        return () => observer.disconnect();
    }, []);

    // ── Fetch records ──────────────────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (startDate) params.startdate = startDate;
            if (endDate) params.enddate = endDate;
            if (minValue) params.minvalue = minValue;
            const data = await apiFetch("/api59", params);
            setRawRecords(data.data);

        } catch (err) {
            setError(err.message || "ไม่สามารถเชื่อมต่อ API ได้");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, minValue]);

    // ── Derived data ───────────────────────────────────────────────────────────
    const rangeData = useMemo(() => {
        const minV = parseFloat(minValue) || 0;
        const processed = processDataAgg(rawRecords);
        return minV > 0 ? processed.filter(r => (r.buy + r.sell) >= minV) : processed;
    }, [rawRecords, minValue]);

    const filteredNoSymbol = useMemo(() => {
        const minV = parseFloat(minValue) || 0;
        const processed = processDataRaw(rawRecords);
        return minV > 0 ? processed.filter(r => (r.buy + r.sell) >= minV) : processed;
    }, [rawRecords, minValue]);

    const aggregatedByDateSymbol = useMemo(() => {
        const minV = parseFloat(minValue) || 0;
        const processed = processDataAgg(rawRecords);
        return minV > 0 ? processed.filter(r => (r.buy + r.sell) >= minV) : processed;
    }, [rawRecords, minValue]);

    // ── handleCheck ────────────────────────────────────────────────────────────
    async function handleCheck() {
        await fetchRecords();
        setSymbolFilter("");
        setIsAllChecked(false);
        setPage(1);
    }

    // ── Auto-fetch on mount ────────────────────────────────────────────────────
    useEffect(() => {
        // fetchRecords();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Update sumValueSymbols ─────────────────────────────────────────────────
    useEffect(() => {
        const allSyms = [...new Set(rangeData.filter(r => (r.buy + r.sell) > 0).map(r => r.symbol))].sort();
        setSumValueSymbols(allSyms);
    }, [rangeData]);

    // ── filtered + sort ────────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        let result;
        if (isAllChecked) {
            result = [...aggregatedByDateSymbol];
        } else if (symbolFilter) {
            result = aggregatedByDateSymbol.filter(r => r.symbol === symbolFilter);
        } else {
            result = [...filteredNoSymbol];
        }

        return result.sort((a, b) => {
            let va, vb;
            if (sortCol === "totalValue") { va = a.buy + a.sell; vb = b.buy + b.sell; }
            else { va = a[sortCol] ?? 0; vb = b[sortCol] ?? 0; }
            if (typeof va === "string") return va.localeCompare(vb) * sortDir;
            return (va - vb) * sortDir;
        });
    }, [filteredNoSymbol, aggregatedByDateSymbol, symbolFilter, isAllChecked, sortCol, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageData = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    function handleSort(col) {
        if (sortCol === col) setSortDir(d => d * -1);
        else { setSortCol(col); setSortDir(-1); }
        setPage(1);
    }

    function numberDisplay(num) {
        const currencyFormat = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        const result = currencyFormat.format(num);
        return result;
    }

    function reset() {
        setStartDate(""); setEndDate(toYMD(new Date()));
        setSymbolFilter(""); setIsAllChecked(false);
        setSumValueSymbols([]); setMinValue("");
        setSortCol("date"); setSortDir(-1);
        setPage(1); setRawRecords([]); setError(null);
        setTimeout(() => fetchRecords(), 0);
    }

    // ─── Styles ───────────────────────────────────────────────────────────────
    const S = {
        wrap: { background: "#0b1120", width: "100%", height: "calc(100vh - var(--top-bar-height, 64px))", display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "#c9d4e8", overflow: "hidden" },
        topBar: { display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: "#0d1526", borderBottom: "1px solid #1e2d45", flexWrap: "wrap", flexShrink: 0 },
        divider: { width: 1, height: 24, background: "#1e2d45", flexShrink: 0 },
        fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
        label: { fontSize: 10, color: "#5a7090", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 },
        select: { background: "#111d30", border: "1px solid #1e2d45", borderRadius: 6, padding: "0 8px", height: 32, color: "#c9d4e8", fontSize: 12, outline: "none", cursor: "pointer", minWidth: 140, fontFamily: "inherit" },
        resetBtn: { background: "transparent", border: "1px solid #1e2d45", borderRadius: 6, height: 32, padding: "0 14px", color: "#7a90b0", fontSize: 18, cursor: "pointer", lineHeight: 1, flexShrink: 0 },
        checkBtn: { height: 32, padding: "0 16px", background: "#2563eb", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, transition: "background .15s" },
        tableWrap: { flex: 1, minHeight: 0, overflowX: "auto", overflowY: "auto", background: "#0b1120" },
        table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
        th: (a = "left") => ({ padding: "10px 14px", textAlign: a, color: "#5a7090", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #1e2d45", borderRight: "1px solid #1e2d45", background: "#0d1526", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", verticalAlign: "middle", position: "sticky", top: 0, zIndex: 10 }),
        th2: (a = "center") => ({ padding: "10px 14px", textAlign: a, color: "#5a7090", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #1e2d45", borderRight: "1px solid #1e2d45", background: "#0d1526", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", verticalAlign: "middle", position: "sticky", top: 36, zIndex: 9 }),
        td: (a = "left") => ({ padding: "9px 14px", borderBottom: "1px solid #1e2d45", borderRight: "1px solid #1e2d45", whiteSpace: "nowrap", color: "#c9d4e8", textAlign: a }),
        pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#0d1526", borderTop: "1px solid #1e2d45", fontSize: 11, color: "#5a7090", flexWrap: "wrap", gap: 8, flexShrink: 0 },
        pageBtn: (active, disabled) => ({ background: active ? "rgba(37,99,235,0.15)" : "transparent", border: `1px solid ${active ? "#2563eb" : "#1e2d45"}`, borderRadius: 5, padding: "3px 9px", color: disabled ? "#253040" : active ? "#60a5fa" : "#7a90b0", fontSize: 11, cursor: disabled ? "default" : "pointer", fontFamily: "inherit" }),
    };

    const pageStart = Math.max(1, Math.min(totalPages - 4, safePage - 2));
    const pageNums = Array.from({ length: Math.min(5, totalPages) }, (_, i) => pageStart + i);

    return (
        <div style={S.wrap}>
            {/* ── TOP BAR ── */}
            <div style={S.topBar}>
                <button style={S.resetBtn} onClick={reset} title="Reset">↺</button>
                <div style={S.divider} />

                <div style={S.fieldGroup}>
                    <span style={S.label}>Start Date</span>
                    <DatePicker value={startDate} onChange={v => { setStartDate(v); setPage(1); }} maxDate={endDate || stats.date_max} />
                </div>

                <div style={S.fieldGroup}>
                    <span style={S.label}>End Date</span>
                    <DatePicker value={endDate} onChange={v => { setEndDate(v); setPage(1); }} minDate={startDate || stats.date_min} maxDate={toYMD(new Date())} />
                </div>

                <div style={S.divider} />

                <div style={S.fieldGroup}>
                    <span style={S.label}>Sum Value</span>
                    <SymbolSelect
                        symbols={sumValueSymbols}
                        value={isAllChecked ? "" : symbolFilter}
                        onChange={v => { setSymbolFilter(v); setIsAllChecked(false); setPage(1); }}
                        onCheckboxClick={() => {
                            if (!isAllChecked) { setIsAllChecked(true); setSymbolFilter(""); }
                            else setIsAllChecked(false);
                            setPage(1);
                        }}
                        isAllChecked={isAllChecked}
                        hasData={sumValueSymbols.length > 0}
                    />
                </div>

                <div style={S.divider} />

                <div style={S.fieldGroup}>
                    <span style={S.label}>&nbsp;</span>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <select style={S.select} value={minValue} onChange={e => { setMinValue(e.target.value); setPage(1); }}>
                            <option value="">Min Value</option>
                            <option value="100">100 THB</option>
                            <option value="1000">1,000 THB</option>
                            <option value="10000">10,000 THB</option>
                            <option value="100000">100,000 THB</option>
                            <option value="500000">500,000 THB</option>
                            <option value="1000000">1,000,000 THB</option>
                            <option value="5000000">5,000,000 THB</option>
                            <option value="10000000">10,000,000 THB</option>
                            <option value="50000000">50,000,000 THB</option>
                            <option value="100000000">100,000,000 THB</option>
                        </select>
                        <button
                            style={{ ...S.checkBtn, opacity: loading ? 0.6 : 1 }}
                            onClick={handleCheck}
                            disabled={loading}
                            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#1d4ed8"; }}
                            onMouseLeave={e => e.currentTarget.style.background = "#2563eb"}
                        >
                            {loading ? "…" : "Search"}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── TABLE ── */}
            <div style={S.tableWrap} ref={tableWrapRef}>
                {loading ? <Spinner /> : error ? <ErrorBanner message={error} onRetry={fetchRecords} /> : (
                    <table style={S.table}>
                        <thead>
                            {/* Row 1 */}
                            <tr>
                                <th rowSpan={2} style={{ ...S.th("center"), color: '#a5abbd', verticalAlign: "middle", top: 0 }} onClick={() => handleSort("date")}>
                                    Date <SortArrow col="date" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                                <th rowSpan={2} style={{ ...S.th("center"), color: '#a5abbd', verticalAlign: "middle", top: 0 }} onClick={() => handleSort("symbol")}>
                                    Symbol <SortArrow col="symbol" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                                <th rowSpan={2} style={{ ...S.th("center"), color: '#a5abbd', verticalAlign: "middle", top: 0 }} onClick={() => handleSort("name")}>
                                    Name <SortArrow col="name" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                                <th colSpan={2} style={{ ...S.th("center"), color: '#a5abbd', borderBottom: "none", top: 0, cursor: "default" }}>
                                    Value (THB)
                                </th>
                                {/* <th rowSpan={2} style={{ ...S.th("center"), verticalAlign: "middle", top: 0 }} onClick={() => handleSort("closePrice")}>
                                    Action Price <SortArrow col="closePrice" sortCol={sortCol} sortDir={sortDir} />
                                </th> */}
                                <th rowSpan={2} style={{ ...S.th("center"), color: '#a5abbd', verticalAlign: "middle", top: 0 }} onClick={() => handleSort("price")}>
                                    Action Price <SortArrow col="avg_price" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                                <th rowSpan={2} style={{ ...S.th("center"), color: '#a5abbd', verticalAlign: "middle", top: 0 }} onClick={() => handleSort("price")}>
                                    Last Price <SortArrow col="price" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                            </tr>
                            {/* Row 2 */}
                            <tr>
                                <th style={{ ...S.th2("center"), color: '#a5abbd', borderTop: "1px solid #1e2d45" }} onClick={() => handleSort("buy")}>
                                    Buy <SortArrow col="buy" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                                <th style={{ ...S.th2("center"), color: '#a5abbd', borderTop: "1px solid #1e2d45" }} onClick={() => handleSort("sell")}>
                                    Sell <SortArrow col="sell" sortCol={sortCol} sortDir={sortDir} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageData.length === 0 ? (
                                <tr><td colSpan={7} style={{ padding: 60, textAlign: "center", color: "#3a506a", fontSize: 14 }}>ไม่พบข้อมูล</td></tr>
                            ) : pageData.map((r, i) => {
                                const isSelected = r.symbol === symbolFilter;
                                return (
                                    <tr key={i}
                                        onMouseEnter={e => e.currentTarget.style.background = "rgba(30,100,200,0.06)"}
                                        onMouseLeave={e => e.currentTarget.style.background = isSelected ? "rgba(37,99,235,0.06)" : "transparent"}
                                        style={{ background: isSelected ? "rgba(37,99,235,0.06)" : "transparent" }}>
                                        <td style={{ ...S.td("center"), color: "#ffffff", fontFamily: "monospace" }}>{r.date}</td>
                                        <td style={{ ...S.td("center"), color: "#60a5fa", fontWeight: 700, letterSpacing: "0.05em", fontFamily: "monospace" }}>{r.symbol}</td>
                                        <td style={{ ...S.td("left"), maxWidth: 180 }}><NameCell name={r.name} /></td>
                                        <td style={{ ...S.td("center"), color: "#22c55e", fontWeight: 600, fontFamily: "monospace" }}>{r.buy ? numberDisplay(r.buy) : "–"}</td>
                                        <td style={{ ...S.td("center"), color: "#ef4444", fontWeight: 600, fontFamily: "monospace" }}>{r.sell ? numberDisplay(r.sell) : "–"}</td>
                                        {/* <td style={{ ...S.td("center"), fontFamily: "monospace" }}>{r.buy  ? fmtVal(r.buy)  : "–"}</td> */}
                                        <td style={{ ...S.td("center"), fontFamily: "monospace" }}>{r.avg_price > 0 ? numberDisplay(r.avg_price) : "–"}</td>
                                        <td style={{ ...S.td("center"), fontFamily: "monospace" }}>{r.price > 0 ? numberDisplay(r.price) : "–"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── PAGINATION ── */}
            {!loading && !error && totalPages > 1 && (
                <div style={S.pagination}>
                    <span>แสดง {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} จาก {filtered.length.toLocaleString()} รายการ</span>
                    <div style={{ display: "flex", gap: 4 }}>
                        <button style={S.pageBtn(false, safePage === 1)} disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>←</button>
                        {pageNums.map(p => <button key={p} style={S.pageBtn(p === safePage, false)} onClick={() => setPage(p)}>{p}</button>)}
                        <button style={S.pageBtn(false, safePage === totalPages)} disabled={safePage === totalPages} onClick={() => setPage(p => p + 1)}>→</button>
                    </div>
                </div>
            )}
        </div>
    );
}